import { useEffect, useRef } from 'react';
import axios from 'axios';
import socketService from '../services/SocketService';
import { useToast } from '../context/ToastContext';
import { playNotificationSound } from '../utils/notificationSound';
import { API_URL } from '../constants/api';
import tokenStore from '../utils/tokenStore';

/**
 * Mounted once while authenticated. Renders nothing — just listens for
 * real-time `issue_assigned` socket events (emitted server-side to the
 * assignee's room when someone assigns them an issue) and surfaces a
 * push-style toast + chime, regardless of which tab is currently open.
 *
 * Mobile browsers suspend/disconnect the socket whenever the screen locks or
 * the tab is backgrounded, so a live event can fire while no one's listening.
 * On reconnect or when the tab becomes visible again, we fetch unread
 * notifications and replay any that arrived after the last one we showed —
 * this is what surfaces assignments that happened during that gap.
 */
const GlobalNotificationListener = ({ onViewIssue }) => {
  const { showToast } = useToast();
  const lastShownAtRef = useRef(Date.now());

  useEffect(() => {
    const surface = (data) => {
      playNotificationSound();
      showToast(data?.message || 'You were assigned a new issue.', 'assignment', {
        title: 'Issue Assigned to You',
        action: { label: 'View issue', onClick: () => onViewIssue?.(data) },
      });
    };

    const handleIssueAssigned = (data) => {
      lastShownAtRef.current = Date.now();
      surface(data);
    };

    const catchUpMissedNotifications = async () => {
      const token = tokenStore.get();
      if (!token) return;
      try {
        const res = await axios.get(`${API_URL}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { unread_only: true, limit: 5 },
        });
        const missed = (Array.isArray(res.data) ? res.data : [])
          .filter((n) => n.type === 'issue_assigned' && new Date(n.created_at).getTime() > lastShownAtRef.current)
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        missed.forEach((n) => {
          lastShownAtRef.current = new Date(n.created_at).getTime();
          surface({ message: n.message, link: n.link });
        });
      } catch (_) {
        // Best-effort catch-up — never let this break the app.
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') catchUpMissedNotifications();
    };

    socketService.on('issue_assigned', handleIssueAssigned);
    socketService.on('connect', catchUpMissedNotifications);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', catchUpMissedNotifications);

    return () => {
      socketService.off('issue_assigned', handleIssueAssigned);
      socketService.off('connect', catchUpMissedNotifications);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', catchUpMissedNotifications);
    };
  }, [showToast, onViewIssue]);

  return null;
};

export default GlobalNotificationListener;
